import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { CATEGORIES } from '../data/products';
import { CategoryInfo } from '../types';
import CategoryContent from '../components/CategoryContent';

const ProductsPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [categoryInfo, setCategoryInfo] = useState<CategoryInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (category) {
      const catInfo = CATEGORIES.find(cat => cat.id === category);
      setCategoryInfo(catInfo || null);
    }
    setLoading(false);
  }, [category]);

  if (loading) {
    return (
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!categoryInfo) {
    return (
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center py-16">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Kategorie nicht gefunden</h1>
            <p className="text-gray-600 mb-8">
              Die angeforderte Kategorie existiert nicht. Bitte versuchen Sie eine andere Kategorie.
            </p>
            <Link 
              to="/"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="mr-2 w-5 h-5" />
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const categoryTitle = categoryInfo?.name || category || 'Produkte';
  const categoryDesc = categoryInfo?.description || '';
  const canonicalSlug = category || '';

  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>{categoryTitle} - Easy Vertrag | Beste Preise & Angebote</title>
        <meta name="description" content={`${categoryDesc} Entdecken Sie unser Angebot an ${categoryTitle} bei Easy Vertrag. Beste Preise von Telekom, Vodafone, O2 und mehr.`} />
        <meta name="keywords" content={`${categoryTitle}, Handyvertrag, Mobilfunk, Easy Vertrag, günstig`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://easy4vertrag.de/produkte/${canonicalSlug}`} />
        <meta property="og:title" content={`${categoryTitle} - Easy Vertrag`} />
        <meta property="og:description" content={`${categoryDesc} Beste Preise und exklusive Angebote.`} />
        <meta property="og:url" content={`https://easy4vertrag.de/produkte/${canonicalSlug}`} />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "${categoryTitle}", "item": "https://easy4vertrag.de/produkte/${canonicalSlug}"}
            ]
          }
        `}</script>
      </Helmet>
      <section
        className="bg-gradient-to-r from-green-800 to-blue-900 text-white py-12"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(30, 64, 175, 0.9), rgba(30, 58, 138, 0.9)), url(${categoryInfo.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Link 
              to="/"
              className="inline-flex items-center text-blue-100 hover:text-white mb-4"
            >
              <ArrowLeft className="mr-1 w-4 h-4" />
              <span>Zurück zur Startseite</span>
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{categoryInfo.name}</h1>
            <p className="text-xl text-blue-100">
              {categoryInfo.description}
            </p>
          </div>
        </div>
      </section>

      <CategoryContent category={category || ''} />
    </div>
  );
};

export default ProductsPage;